/**
 * Shared outcome / reliability analytics props (web + mobile).
 * Keep this set tight: success, errorMessage, failureKind (+ optional httpStatus).
 */

export type AnalyticsFailureKind =
  | 'connectivity'
  | 'http'
  | 'validation'
  | 'duplicate_account'
  | 'password_policy'
  | 'unknown'

/** User-facing copy when the client could not reach the server. */
export const FRIENDLY_CONNECTIVITY_MESSAGE =
  "We weren't able to reach the server right now. Please try again."

export function isConnectivityErrorMessage(message: string): boolean {
  const msg = message.trim()
  if (!msg) return false
  return (
    /^load failed$/i.test(msg) ||
    /failed to fetch/i.test(msg) ||
    /networkerror when attempting to fetch/i.test(msg) ||
    /network request failed/i.test(msg) ||
    /the internet connection appears to be offline/i.test(msg) ||
    /network.*(fail|error|unreachable)/i.test(msg) ||
    /timed? ?out/i.test(msg)
  )
}

export function isConnectivityFailure(error: unknown): boolean {
  if (!(error instanceof TypeError) && !(error instanceof Error)) return false
  // oidc-client-ts ErrorTimeout when authority metadata/token requests hang.
  if ((error as { name?: string }).name === 'ErrorTimeout') return true
  return isConnectivityErrorMessage(error.message ?? '')
}

export function outcomeSuccessProps(): { success: true } {
  return { success: true }
}

export function outcomeFailureProps(input: {
  errorMessage: string
  failureKind: AnalyticsFailureKind
  httpStatus?: number
}): {
  success: false
  errorMessage: string
  failureKind: AnalyticsFailureKind
  httpStatus?: number
} {
  const out: {
    success: false
    errorMessage: string
    failureKind: AnalyticsFailureKind
    httpStatus?: number
  } = {
    success: false,
    errorMessage: input.errorMessage,
    failureKind: input.failureKind,
  }
  if (input.httpStatus != null) out.httpStatus = input.httpStatus
  return out
}

/** Classify a thrown value into failureKind + errorMessage for analytics. */
export function classifyThrownFailure(error: unknown): {
  failureKind: AnalyticsFailureKind
  errorMessage: string
} {
  if (isConnectivityFailure(error)) {
    return {
      failureKind: 'connectivity',
      errorMessage: error instanceof Error ? error.message : String(error),
    }
  }
  if (error instanceof Error) {
    return { failureKind: 'unknown', errorMessage: error.message }
  }
  return { failureKind: 'unknown', errorMessage: String(error) }
}
